import { api } from '../api';
import { useApi } from '../lib/useApi';
import { AreaChart } from '../lib/charts';
import Drawer from '../components/Drawer';
import { Card, CardHead, ErrorState, Loading, PlanBadge, Usage } from '../components/ui';
import { daysBetween, fmtDate, gb, hrs, n0, pct, plural, rub } from '../lib/format';
import { STATUS } from '../lib/plans';
import { useAuth } from '../components/AuthProvider';

const byCode = (usage, code) => usage.find((u) => u.code === code) || { used: 0, max: 0 };

export default function WorkspaceDrawer({ id, onClose, onOpenUser }) {
  return (
    <Drawer open={!!id} onClose={onClose}>
      {id ? <Body id={id} onClose={onClose} onOpenUser={onOpenUser} /> : null}
    </Drawer>
  );
}

function Body({ id, onClose, onOpenUser }) {
  const { canEdit } = useAuth();
  const { data: o, loading, error, reload } = useApi(() => api.workspace(id), [id]);
  const series = useApi(() => api.workspaceSeries(id, { days: 60 }), [id]);

  if (loading) return <div className="dr-body"><Loading what="пространство" /></div>;
  if (error) return <div className="dr-body"><ErrorState error={error} onRetry={reload} /></div>;

  const st = STATUS[o.subscription.status] || { ru: o.subscription.status, cls: 'b-expired' };
  const seats = byCode(o.usage, 'AVAILABLE_USER_COUNT');
  const space = byCode(o.usage, 'AVAILABLE_SPACE_MB');
  const rate = space.max ? space.used / space.max : 0;
  const mailto = `mailto:${o.owners[0]}?subject=${encodeURIComponent('7cut · ' + o.title)}&body=${encodeURIComponent('Здравствуйте!\n\n')}`;

  return (
    <>
      <div className="dr-head">
        <button className="close" onClick={onClose} aria-label="закрыть">✕</button>
        <h2>{o.title}</h2>
        <div>
          <PlanBadge plan={o.plan.name} published={o.plan.published} />{' '}
          <span className={`badge ${st.cls}`}>{st.ru}</span>
          <span className="muted" style={{ fontSize: 12, marginLeft: 6 }}>
            РП №{o.id} · создано {fmtDate(o.createdAt)}
          </span>
        </div>
      </div>

      <div className="dr-body">
        <div className="grid cards-2">
          <Card>
            <div className="k-label">людей в команде</div>
            <div className="k-val" style={{ fontSize: 30 }}>{seats.used}<small>из {seats.max}</small></div>
            <div style={{ marginTop: 10 }}><Usage used={seats.used} max={seats.max} /></div>
          </Card>
          <Card>
            <div className="k-label">занято места</div>
            <div className="k-val" style={{ fontSize: 30 }}>{gb(space.used)}<small>из {gb(space.max)}</small></div>
            <div style={{ marginTop: 10 }}><Usage used={space.used} max={space.max} fmt={gb} updating={space.updating} /></div>
          </Card>
        </div>

        {rate > 0.85 ? (
          <div className="note warn">
            Место почти кончилось — {pct(rate)}.{' '}
            {o.plan.name === 'База'
              ? 'На «Базе» докупить нельзя, только переход на ПРО.'
              : 'Можно предложить докупку: +299 ₽ за 10 ГБ.'}
          </div>
        ) : null}

        <div>
          <h3 className="dr-h">подписка</h3>
          <dl className="kv">
            <dt>тариф</dt>
            <dd>
              {o.plan.name}
              {o.subscription.status === 'ACTIVE' ? (o.subscription.annual ? ', годовая оплата (−15%)' : ', помесячно') : ''}
              {o.plan.published ? null : <div className="t-sub">не на витрине — подписку выдают по прямой ссылке</div>}
            </dd>
            <dt>состояние</dt>
            <dd><span className={`badge ${st.cls}`}>{st.ru}</span></dd>
            {o.subscription.trialEndDate ? (
              <>
                <dt>триал до</dt>
                <dd>{fmtDate(o.subscription.trialEndDate)} — осталось{' '}
                  {Math.max(0, daysBetween(o.subscription.trialEndDate, new Date()))}{' '}
                  {plural(daysBetween(o.subscription.trialEndDate, new Date()), 'день', 'дня', 'дней')}</dd>
              </>
            ) : null}
            {o.subscription.renewAt ? (
              <><dt>следующее списание</dt><dd>{fmtDate(o.subscription.renewAt)} · {rub(o.monthlyRevenue)}</dd></>
            ) : null}
            {o.retention ? (
              <><dt>удаление данных</dt><dd style={{ color: 'var(--bad)' }}>{fmtDate(o.retention.deadlineAt)}</dd></>
            ) : null}
            <dt>доплаты</dt>
            <dd>
              {o.addons.seats || o.addons.space10
                ? [o.addons.seats ? `+${o.addons.seats} ${plural(o.addons.seats, 'место', 'места', 'мест')}` : '',
                   o.addons.space10 ? `+${o.addons.space10 * 10} ГБ` : ''].filter(Boolean).join(', ')
                : <span className="muted">нет</span>}
            </dd>
            <dt>в месяц</dt>
            <dd className="mono">{o.monthlyRevenue ? rub(o.monthlyRevenue) : <span className="muted">не платит</span>}</dd>
            <dt>владелец</dt>
            <dd>{o.owners.map((e) => <div key={e}>{e}</div>)}</dd>
          </dl>
        </div>

        <Card className="pad">
          <CardHead title="комментарии за 60 дней" caption={`всего за жизнь РП — ${n0(o.comments)}`} />
          {series.loading ? <Loading what="график" />
            : series.error ? <ErrorState error={series.error} onRetry={series.reload} />
            : <AreaChart data={series.data.map((d) => ({ date: d.date, v: d.value }))} height={130} />}
        </Card>

        <div className="grid cards-3">
          <Card><div className="k-label">проектов</div>
            <div className="k-val" style={{ fontSize: 26 }}>{o.projects}<small>из {o.projectsLimit || '∞'}</small></div></Card>
          <Card><div className="k-label">видео</div>
            <div className="k-val" style={{ fontSize: 26 }}>{n0(o.videos)}</div></Card>
          <Card><div className="k-label">хронометраж</div>
            <div className="k-val" style={{ fontSize: 26 }}>{hrs(o.durationSec)}</div></Card>
        </div>

        <div>
          <h3 className="dr-h">команда</h3>
          <div className="list">
            {o.team.map((u) => (
              <button className="row" key={u.id} onClick={() => onOpenUser && onOpenUser(u.id)}>
                <span className="rn">
                  {u.firstName} {u.lastName} {u.blocked ? <span className="badge b-blocked">блок</span> : null}
                  <div className="t-sub">{u.email}</div>
                </span>
                <span className="rv">
                  {u.role === 'OWNER' ? 'владелец' : 'редактор'}{u.roleStatus === 'PENDING' ? ' · не вошёл' : ''}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div>
          <h3 className="dr-h">действия</h3>
          <div className="act">
            <a className="btn" href={mailto}>написать владельцу</a>
            <CopyButton value={o.owners.join(', ')} />
            {canEdit ? (
              <>
                <button className="btn">сменить статус подписки</button>
                <button className="btn">продлить триал</button>
                <button className="btn danger">удалить подписку</button>
              </>
            ) : null}
          </div>
          <div className="note" style={{ marginTop: 12 }}>
            «Написать владельцу» открывает почтовую программу с подставленным адресом и темой —
            письмо уходит лично от вас. Каждое действие пишется в журнал: кто, когда, что изменил.
          </div>
        </div>
      </div>
    </>
  );
}

function CopyButton({ value }) {
  return (
    <button
      className="btn"
      onClick={(e) => {
        navigator.clipboard?.writeText(value);
        const btn = e.currentTarget;
        const text = btn.textContent;
        btn.textContent = 'скопировано';
        setTimeout(() => { btn.textContent = text; }, 1600);
      }}
    >
      скопировать почту
    </button>
  );
}
